import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { ShowModelSummary, ShowSummary } from '@fetmode/types';
import { resolveApiUrl } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';
import { formatUsername } from '@fetmode/utils';
import { Link } from 'react-router-dom';

import { useAuth } from '../auth/index.js';
import { useShowRealtime } from '../shows/useShowRealtime.js';
import { camBadgeClass, camBadgeLabel } from '../home/cam-show-badge.js';
import { loadOnboardingTags } from '../experience/onboarding-tags.js';
import { GenderIcon } from '../../shared/components/GenderIcon/index.js';
import {
  COMPACT_GRID_PAGE_SIZE,
  GRID_PAGE_SIZE,
  ThumbGridPager,
  usePagedItems,
} from '../../shared/components/ThumbGridPager/index.js';
import { avatarUrl, camThumbUrl } from '../../shared/placeholder-media.js';
import { CommunityJoinLanding } from './CommunityJoinLanding.js';
import '../../shared/styles/thumb-grid.css';
import './CommunityHomePage.css';

const apiBaseUrl = resolveApiUrl(import.meta.env);

export function CommunityHomePage(): ReactElement {
  const { api, user } = useAuth();
  const [online, setOnline] = useState<ShowSummary[]>([]);
  const [suggestions, setSuggestions] = useState<ShowModelSummary[]>([]);
  const [following, setFollowing] = useState<ReadonlySet<string>>(() => new Set());
  const [busyUsername, setBusyUsername] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const selectedTags = loadOnboardingTags().tags;
  const peoplePaging = usePagedItems(suggestions, COMPACT_GRID_PAGE_SIZE);
  const livePaging = usePagedItems(online, COMPACT_GRID_PAGE_SIZE);

  const refresh = useCallback(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    void Promise.all([
      api.listCams(undefined, { onlinePage: 1, offlinePage: 1, limit: GRID_PAGE_SIZE }),
      api.listFollowing().catch(() => ({ following: [] })),
    ])
      .then(([cams, followingResult]) => {
        setOnline(cams.online);
        const followed = new Set(followingResult.following.map((entry) => entry.username));
        setFollowing(followed);
        setSuggestions(cams.offline.filter((model) => !followed.has(model.username)));
        setError(null);
      })
      .catch(() => {
        setError(t('common.community.loadError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [api, user]);

  useEffect(() => {
    setIsLoading(true);
    refresh();
  }, [refresh]);

  useShowRealtime(
    apiBaseUrl,
    { cams: Boolean(user) },
    {
      onCamsChanged: () => {
        refresh();
      },
    },
  );

  if (!user) {
    return <CommunityJoinLanding />;
  }

  const handleFollow = (username: string): void => {
    setBusyUsername(username);
    void api
      .followModel(username)
      .then(() => {
        setFollowing((current) => new Set([...current, username]));
        setSuggestions((current) => current.filter((model) => model.username !== username));
      })
      .catch(() => {
        setError(t('common.community.followError'));
      })
      .finally(() => {
        setBusyUsername(null);
      });
  };

  return (
    <section className="community-home">
      <header className="community-home__hero">
        <p className="community-home__eyebrow">{t('common.community.eyebrow')}</p>
        <h1 className="community-home__title">{t('common.community.title')}</h1>
        <p className="community-home__subtitle">
          {selectedTags.length > 0
            ? t('common.community.subtitleTagged').replace(
                '{{tags}}',
                selectedTags.slice(0, 4).join(', '),
              )
            : t('common.community.subtitle')}
        </p>
      </header>

      {error ? <p className="fm-error">{error}</p> : null}

      <section className="community-home__section" aria-labelledby="community-people-heading">
        <div className="community-home__section-head">
          <h2 id="community-people-heading">{t('common.community.peopleTitle')}</h2>
          <p>{t('common.community.peopleHint')}</p>
        </div>

        {isLoading ? (
          <p className="community-home__muted">{t('common.community.loading')}</p>
        ) : suggestions.length === 0 ? (
          <p className="community-home__muted">{t('common.community.peopleEmpty')}</p>
        ) : (
          <ul className="community-home__people">
            {peoplePaging.pageItems.map((model) => {
              const isFollowing = following.has(model.username);
              return (
                <li key={model.username} className="community-home__person">
                  <Link to={`/@${model.username}`} className="community-home__person-link">
                    <img
                      src={avatarUrl(model.username)}
                      alt=""
                      width={56}
                      height={56}
                      className="community-home__avatar"
                    />
                    <span className="community-home__person-copy">
                      <strong>{model.displayName}</strong>
                      <span>{formatUsername(model.username)}</span>
                    </span>
                  </Link>
                  <Button
                    label={
                      isFollowing
                        ? t('common.community.following')
                        : busyUsername === model.username
                          ? t('common.community.followingBusy')
                          : t('common.community.follow')
                    }
                    variant="secondary"
                    disabled={isFollowing || busyUsername === model.username}
                    onClick={() => {
                      handleFollow(model.username);
                    }}
                  />
                </li>
              );
            })}
          </ul>
        )}
        <ThumbGridPager
          page={peoplePaging.page}
          total={peoplePaging.total}
          pageSize={COMPACT_GRID_PAGE_SIZE}
          label={t('common.community.peopleTitle')}
          onPageChange={peoplePaging.setPage}
        />
      </section>

      <section className="community-home__section" aria-labelledby="community-live-heading">
        <div className="community-home__section-head">
          <h2 id="community-live-heading">{t('common.community.liveTitle')}</h2>
          <p>{t('common.community.liveHint')}</p>
          <Link to="/cams" className="community-home__see-all">
            {t('common.community.seeAllCams')}
          </Link>
        </div>

        {isLoading ? (
          <p className="community-home__muted">{t('common.community.loading')}</p>
        ) : online.length === 0 ? (
          <p className="community-home__muted">{t('common.home.noLiveShows')}</p>
        ) : (
          <div className="thumb-grid community-home__live-grid">
            {livePaging.pageItems.map((show) => {
              const onBreak = show.status === 'PAUSED';
              const isAway = show.status === 'AWAY';
              return (
                <article
                  key={show.id}
                  className={`thumb-card ${camBadgeClass(show.type).replace('thumb-card__badge--', 'thumb-card--')}`}
                >
                  <div className="thumb-card__thumb">
                    <Link
                      to={`/@${show.model.username}`}
                      className="thumb-card__hit"
                      aria-label={show.model.displayName}
                    >
                      <img
                        src={camThumbUrl(show.model.username)}
                        alt=""
                        loading="lazy"
                        width={640}
                        height={360}
                      />
                      <span className={`thumb-card__badge ${camBadgeClass(show.type)}`}>
                        {isAway
                          ? t('common.show.away')
                          : onBreak
                            ? t('common.show.onBreak')
                            : camBadgeLabel(show.type)}
                      </span>
                      <div className="thumb-card__meta">
                        <div className="thumb-card__title-row">
                          <GenderIcon gender={show.model.gender} />
                          <h3 className="thumb-card__title">{show.model.displayName}</h3>
                        </div>
                      </div>
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        )}
        <ThumbGridPager
          page={livePaging.page}
          total={livePaging.total}
          pageSize={COMPACT_GRID_PAGE_SIZE}
          label={t('common.community.liveTitle')}
          onPageChange={livePaging.setPage}
        />
      </section>
    </section>
  );
}
